import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const LANGS = { en: 'EN', es: 'ES', de: 'DE' };

export function Navbar() {
  const { t, language, setLanguage } = useLanguage();
  const { isAdmin, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/inventory', label: t('nav.inventory') },
    { to: '/impressions', label: t('nav.impressions') },
    { to: '/partners', label: t('nav.partners') },
    { to: '/about', label: t('nav.about') },
    { to: '/contact', label: t('nav.contact') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="main-navbar"
      className="fixed top-0 w-full z-50 border-b border-white/5 bg-black/60 backdrop-blur-xl supports-[backdrop-filter]:bg-black/30"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_global-auto-export-2/artifacts/v8jve8fv_Partner4cars_logoL%20wit.png"
              alt="Partners4Cars"
              className="h-12 md:h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.to.replace('/', '') || 'home'}`}
                className={`text-xs uppercase tracking-[0.2em] transition-colors duration-200 ${
                  isActive(link.to) ? 'text-white' : 'text-zinc-500 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-zinc-400 hover:text-white text-xs tracking-widest uppercase gap-1"
                  data-testid="language-switcher"
                >
                  {LANGS[language]}
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#121212] border-zinc-800 min-w-[80px]">
                {Object.entries(LANGS).map(([code, label]) => (
                  <DropdownMenuItem
                    key={code}
                    onClick={() => setLanguage(code)}
                    data-testid={`lang-option-${code}`}
                    className={`text-xs tracking-widest cursor-pointer ${
                      language === code ? 'text-[#ED2227]' : 'text-zinc-400'
                    }`}
                  >
                    {label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {isAdmin ? (
              <div className="flex items-center gap-3">
                <Link to="/admin">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs tracking-widest uppercase text-zinc-400 hover:text-white"
                    data-testid="admin-dashboard-link"
                  >
                    {t('nav.dashboard')}
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={logout}
                  className="text-xs tracking-widest uppercase text-zinc-500 hover:text-[#ED2227]"
                  data-testid="logout-button"
                >
                  {t('nav.logout')}
                </Button>
              </div>
            ) : (
              <Link to="/admin/login">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs tracking-widest uppercase text-zinc-400 hover:text-white"
                  data-testid="admin-login-link"
                >
                  {t('nav.login')}
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile toggle */}
          <button
            className="md:hidden text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <div className="md:hidden py-6 border-t border-white/5 space-y-4" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className={`block text-sm uppercase tracking-widest py-2 ${
                  isActive(link.to) ? 'text-white' : 'text-zinc-500'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="flex gap-4 pt-4 border-t border-white/5">
              {Object.entries(LANGS).map(([code, label]) => (
                <button
                  key={code}
                  onClick={() => { setLanguage(code); setMobileOpen(false); }}
                  className={`text-xs tracking-widest ${
                    language === code ? 'text-[#ED2227]' : 'text-zinc-500'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {isAdmin ? (
              <div className="flex gap-4 pt-2">
                <Link to="/admin" onClick={() => setMobileOpen(false)} className="text-sm text-zinc-400 uppercase tracking-widest">
                  {t('nav.dashboard')}
                </Link>
                <button onClick={() => { logout(); setMobileOpen(false); }} className="text-sm text-zinc-500 uppercase tracking-widest">
                  {t('nav.logout')}
                </button>
              </div>
            ) : (
              <Link
                to="/admin/login"
                onClick={() => setMobileOpen(false)}
                className="block text-sm text-zinc-400 uppercase tracking-widest pt-2"
              >
                {t('nav.login')}
              </Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
